import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSession } from '../../context/SessionContext';

function ProgressBar() {
  const location = useLocation();
  const [currentPath, setCurrentPath] = useState(location.pathname);
  const { sessionData, isLoading } = useSession();
  
  // Update currentPath when location changes
  useEffect(() => {
    setCurrentPath(location.pathname);
    // console.log("Location changed to:", location.pathname);
  }, [location.pathname]);
  
  // Define all possible steps in the application flow
  const allSteps = [
    { 
      name: 'Resume', 
      description: 'Share your info',
      paths: ['resume', 'notice'],
      stepKey: 'step_upload_resume'
    },
    { 
      name: 'Intro', 
      description: 'Record introduction',
      paths: ['videointro'],
      stepKey: 'step_video_intro'
    },
    { 
      name: 'Questionnaire', 
      description: 'Answer questions',
      paths: ['questionnaire'],
      stepKey: 'step_questionnaire'
    },
    { 
      name: 'Challenge', 
      description: 'Demonstrate skills',
      paths: ['challenge'],
      stepKey: 'step_challenge'
    },
    { 
      name: 'Done', 
      description: 'Complete application',
      paths: ['finished'],
      stepKey: 'final'
    }
  ];
  
  // Filter steps based on what's enabled in session data
  const getEnabledSteps = () => {
    if (isLoading || !sessionData || !sessionData.steps) {
      // Return all steps if session data is not loaded yet
      return allSteps;
    }
    
    return allSteps.filter(step => {
      // Always show Done step
      if (step.stepKey === 'final') {
        return true;
      }
      // Show all other steps only if they're enabled in sessionData.steps
      return sessionData.steps && sessionData.steps[step.stepKey];
    });
  };
  
  const steps = getEnabledSteps();
  
  // Determine the current step based on the URL path
  const getCurrentStep = () => {
    // Extract the current page from the URL
    // URL format is typically /page/eid
    const urlParts = currentPath.split('/').filter(Boolean);
    const currentPage = urlParts.length > 0 ? urlParts[0] : '';
    
    // Special case for root path
    if (currentPath === '/' || currentPath.match(/^\/[^\/]+$/)) {
      return 0; // Resume is the first step now
    }
    
    // Check each step
    for (let i = 0; i < steps.length; i++) {
      if (steps[i].paths.some(path => currentPage === path)) {
        return i;
      }
    }
    
    return 0; // Default to first step if no match
  };
  
  const currentStep = getCurrentStep();
  const totalSteps = steps.length - 1;
  const progressPercentage = (currentStep / totalSteps) * 100;
  
  return (
    <div className="flex flex-col items-center w-full">
      <div className="flex justify-between w-full mb-2">
        {steps.map((step, index) => (
          <div 
            key={index} 
            className={`text-xs font-medium relative flex flex-col items-center px-4 ${
              index === currentStep 
                ? 'text-black' 
                : index < currentStep 
                  ? 'text-black' 
                  : 'text-gray-400'
            }`}
          >
            <div className="whitespace-nowrap">{step.name}</div>
          </div>
        ))}
      </div>
      <div className="w-full bg-gray-200 rounded-full h-1 mt-1">
        <div 
          className="h-1 rounded-full transition-all duration-300" 
          style={{ width: `${progressPercentage}%`, backgroundColor: '#33C6B6' }}
        ></div>
      </div>
    </div>
  );
}

export default ProgressBar;
